import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { api } from '../services/api';

interface ThreadMessage {
  direction: 'guest' | 'bot' | 'agent';
  content: string;
  sentAt: string;
}

interface ThreadResponse {
  taskId: string;
  sessionId: string;
  available: boolean;
  messages: ThreadMessage[];
}

interface Props {
  taskId: string;
  /** Reply is only possible while the guest's 24h WhatsApp window is open. */
  onReplySent?: () => void;
}

// The guest is the only one on the left; bot and operator both speak for the hotel.
function isFromGuest(message: ThreadMessage): boolean {
  return message.direction === 'guest';
}

function messageLabel(message: ThreadMessage): string {
  if (message.direction === 'agent') return 'Siz';
  if (message.direction === 'bot') return 'Bot';
  return '';
}

function messageTime(message: ThreadMessage): string {
  const parsed = new Date(message.sentAt);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleString();
}

export default function WhatsappTaskThread({ taskId, onReplySent }: Props) {
  const [thread, setThread] = useState<ThreadResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const loadThread = useCallback(async () => {
    try {
      const response = await api.get(`/service-tasks/${taskId}/whatsapp-thread`);
      setThread(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Konuşma yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    loadThread();
  }, [loadThread]);

  // The guest keeps writing while the task is open; poll so their replies appear
  // without the operator reloading. Skipped mid-send to avoid racing our own POST.
  useEffect(() => {
    const interval = setInterval(() => {
      if (!sending) loadThread();
    }, 10000);
    return () => clearInterval(interval);
  }, [loadThread, sending]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread]);

  const handleSend = async () => {
    const content = reply.trim();
    if (!content) return;

    setSending(true);
    setError(null);
    try {
      await api.post(`/service-tasks/${taskId}/whatsapp-reply`, { content });
      setReply('');
      await loadThread();
      onReplySent?.();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Mesaj gönderilemedi.');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} />
        <Typography variant="subtitle2">
          WhatsApp konuşması{thread?.sessionId ? ` · +${thread.sessionId}` : ''}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {thread && !thread.available && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Bu task, konuşma kaydı tutulmaya başlanmadan önce açılmış. Geçmişi
          görüntülenemiyor; cevap yazmak yine de çalışır.
        </Alert>
      )}

      <Paper
        variant="outlined"
        sx={{ maxHeight: 260, overflowY: 'auto', p: 1.5, mb: 2, bgcolor: 'background.default' }}
      >
        {thread?.messages?.length ? (
          thread.messages.map((message, index) => {
            const fromGuest = isFromGuest(message);
            return (
              <Box
                key={index}
                sx={{ display: 'flex', justifyContent: fromGuest ? 'flex-start' : 'flex-end', mb: 1 }}
              >
                <Box
                  sx={{
                    maxWidth: '75%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: fromGuest ? 'grey.200' : '#DCF8C6',
                    color: 'text.primary',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.content}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[messageLabel(message), messageTime(message)].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              </Box>
            );
          })
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            Henüz mesaj yok.
          </Typography>
        )}
        <div ref={bottomRef} />
      </Paper>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          placeholder="Misafire aynı hattan cevap yazın..."
          value={reply}
          onChange={(event) => setReply(event.target.value)}
          disabled={sending}
        />
        <Button variant="contained" onClick={handleSend} disabled={sending || !reply.trim()}>
          {sending ? <CircularProgress size={18} /> : 'Gönder'}
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        Misafir 24 saatten uzun süredir yazmadıysa WhatsApp cevabı reddedebilir.
      </Typography>
    </Box>
  );
}
